import { message } from "antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { GRAPHQL_URL } from "../config";

/**
 * Download a file (presigned S3 export URL) as robustly as possible.
 *
 * Fetches the file as a blob and saves it via a temporary anchor — this forces
 * a real download even for cross-origin URLs (where the `download` attribute is
 * otherwise ignored). If the fetch fails (CORS, network, blocked), it falls
 * back to opening the URL in a new tab so the user can still get the file.
 *
 * Used for the `export*` GraphQL flows (checkout / reviews / media / call /
 * email dashboards).
 *
 * @param {string} fileUrl - Direct/presigned URL of the file.
 * @param {string} [fileName] - Optional download filename. Falls back to the
 *   URL's basename, then "download".
 * @returns {Promise<boolean>} True once a download/new-tab was triggered, false
 *   if no URL was given.
 */
export const downloadFileFromUrl = async (fileUrl, fileName) => {
  if (isEmpty(fileUrl)) return false;
  const name =
    fileName || decodeURIComponent(fileUrl.split("/").pop()?.split("?")[0] || "download");
  try {
    const response = await fetch(fileUrl, { mode: "cors" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    // Fallback: the direct download didn't work — open the file in a new tab.
    // eslint-disable-next-line no-console
    console.warn("[download] fetch failed, opening in new tab", err);
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  }
  return true;
};

export const mediaDownloader = async imageSrc => {
  try {
    if (isEmpty(imageSrc) || !imageSrc) {
      message.error("This media file cannot downloaded, please contact support.");
    }
    const fileName = imageSrc.split("/").pop();
    // Extract the file extension from the file name
    const extension = fileName.includes(".") ? fileName.split(".").pop() : "jpg";
    const res = await fetch(
      `${GRAPHQL_URL.replace("graphql", "download-image")}?url=${encodeURIComponent(imageSrc)}`,
      { method: "GET" }
    );
    const blobImage = await res.blob();
    const url = window.URL.createObjectURL(blobImage);
    const a = document.createElement("a");
    a.href = url;
    a.download = `smai-media-file-${dayjs()}.${extension}`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 60000);
    a.remove();
  } catch (err) {
    message.error(`Something went wrong, Error: - ${err.message}`);
    console.error("Error in mediaDownloader", err);
  }
};

/**
 * Downloads an audio file from a given URL using pure JavaScript
 * @param {string} audioSrc - The URL of the audio file to download
 * @param {string} [customFileName] - Optional custom file name for the downloaded audio
 * @param {string} [callTimestamp] - Optional timestamp of the call for the filename
 */
export const audioDownloader = async (audioSrc, customFileName, callTimestamp, callback) => {
  try {
    if (isEmpty(audioSrc) || !audioSrc) {
      message.error("This audio file cannot be downloaded, please contact support.");
      return;
    }

    // Get the file extension from the URL
    const fileName = audioSrc.split("/").pop();
    const extension = fileName.includes(".") ? fileName.split(".").pop() : "mp3";

    // Fetch the audio file directly
    const response = await fetch(audioSrc);

    if (!response.ok) {
      throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
    }

    // Get the audio file as a blob
    const blob = await response.blob();

    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create an anchor element for downloading
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;

    // Use a formatted timestamp if provided, otherwise use current time
    const timestamp = callTimestamp
      ? dayjs(callTimestamp).format("YYYY-MM-DD_HH-mm")
      : dayjs().format("YYYY-MM-DD_HH-mm");

    // Set the download attribute with the filename
    a.download = `${customFileName || `call-recording-${timestamp}`}.${extension}`;

    // Append to the document and trigger click
    document.body.appendChild(a);
    a.click();

    // Clean up
    document.body.removeChild(a);

    // Revoke the URL after a delay to ensure download has started
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 60000);
  } catch (err) {
    message.error(`Could not download audio file. Error: ${err.message}`);
    if (callback) {
      callback();
    }
    console.error("Error in audioDownloader:", err);
  }
};
